/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';
import moment from 'moment';
import {LINE_ITEM_OBJECTIVE_TYPE} from '../common/constants/resource-constants';
import {DEFAULT_MARKETPLACE_OBJECTIVE_FIELDS} from '../../components/session/local-network-profile';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {ALL_TARGETING_TYPES} from '../targeting/targeting-constants';
import {getCountryName} from '../common/directives/country-select/countries';
import {formatIsoCurrency} from '../../components/util/iso-currency-format';
import {ogTranslate} from '../common/filters/og-translate';
import {formatLocaleNumber} from '../common/locale/locale-number-format';
import {TeamApiService} from '../admin/team/team-api.service';

const maxValues = {
  "AUD": 200,
  "EUR": 200,
  "THB": 4000,
  "NOK": 1500,
  "USD": 200,
  "SEK": 1500,
  "DKK": 1000,
  "SGD": 200,
  "IDR": 2000000,
  "VND": 3000000
};

export const productCreativeTag = 'tct918cp25c93wpm';
export const officialProductSimple = 'pzlwjm3nmddkr3c3';
export const officialHighImpactSimple = '3m0xt7zh7bmmjwl7';
export const officialHighImpactThirdParty = 'hbh117crs8yrxx7n';
export const officialPoliticalProduct = '9cr2rkpfck33hcy5';
export const simpleMarketplaceProducts = [officialProductSimple, officialHighImpactSimple, officialHighImpactThirdParty, productCreativeTag, officialPoliticalProduct];
export const creativeTypeOptions = [{id: officialProductSimple, name: 'Regular', description: 'Deliver regular ads'}, {id: officialHighImpactSimple, name: 'High Impact', description: 'Deliver advanced ads in high-impact formats'}, {id: officialHighImpactThirdParty, name: 'Third-Party High Impact', description: 'Deliver advanced ads in high-impact formats via third-party tags from providers such as Adnami or Seenthis'}];

interface ObjectiveSummary {
  number: any;
  key: string;
  translation: string;
  prettyNumber?: string;
}

export function isBidTooHigh(currentValue: any, originalValue: any): false | {amount: any; currency: any} {
  const originalMaxValue = maxValues[_.get(originalValue, ['currency'], 'NOTHING')];
  if (originalMaxValue && originalValue.amount >= originalMaxValue) {
    return false;
  }
  const maxValue = maxValues[_.get(currentValue, ['currency'], "NOTHING")];
  return (maxValue && currentValue.amount >= maxValue) ? {amount: maxValue, currency: currentValue.currency} : false;
}

export function setLineItemDefaults(LocalNetworkProfile: any, ctrl: any, model: any): void {
  const networkDefaults = LocalNetworkProfile.getDefaults();
  ctrl.showCpmFees = networkDefaults.showCpmFees;
  ctrl.showExternalReference = networkDefaults.showExternalReference;
  if (ctrl.isNew) {
    model.startDate = moment().toDate();
    model.endDate = moment.tz(LocalNetworkProfile.get('timeZone') || 'utc').add(networkDefaults.lineItemDaysApart, 'day').endOf('day').toDate();

    if (ctrl.isAxAdvertiser) {
      model.userState = networkDefaults.marketplaceLineItemUserState;
    } else {
      model.userState = ctrl.isSelfService ? "PROPOSED" : networkDefaults.lineItemUserState || 'APPROVED';
    }
    if (!model.userState) {
      delete model.userState;
    }
    model.deduplicationLevel = networkDefaults.lineItemDeduplicationLevel;
    if (!model.deduplicationLevel) {
      delete model.deduplicationLevel;
    }
    _.set(ctrl, ['data', 'hasStats'], true);
    if (!ctrl.isAxAdvertiser && _.isObject(networkDefaults.cpm)) {
      model.bidSpecification = model.bidSpecification || {};
      model.bidSpecification.cpm = _.cloneDeep(networkDefaults.cpm);
    }
    if (_.isArray(networkDefaults.cpmFees)) {
      model.bidSpecification = model.bidSpecification || {};
      model.bidSpecification.cpmFees = _.cloneDeep(networkDefaults.cpmFees);
    }
  }
}

export function lineItemModDates(adnTimeShifter: any, model: any, modStartDate: any, modEndDate: any): void {
  // DateTimePickerComponent presents UTC instants in the network timezone and
  // converts user input back to UTC. Do not apply the legacy browser-time shim.
  void adnTimeShifter;
  const startDate = modStartDate ? (_.isDate(modStartDate) ? modStartDate : new Date(modStartDate)) : null;
  const endDate = modEndDate ? (_.isDate(modEndDate) ? modEndDate : new Date(modEndDate)) : null;
  model.startDate = startDate && !Number.isNaN(startDate.getTime()) ? startDate : null;
  model.endDate = endDate && !Number.isNaN(endDate.getTime()) ? endDate : null;
}

function hasConfiguredFormValue(value: any): boolean {
  if (_.isNil(value) || value === '') {
    return false;
  }
  if (_.isObject(value)) {
    return _.has(value, 'amount') &&
      !_.isNil(_.get(value, 'amount')) &&
      _.get(value, 'amount') !== '';
  }
  return true;
}

export const lineItemCommons = function(
  ctrl: any,
  translate: any,
  appLocale: any,
  LocalNetworkProfile: any,
  LocalUserPermissions: any,
  adnTimeShifter: any,
  localNetworkInfo: LocalNetworkInfo,
  pParam?: string
): any {
  const param = pParam || 'model';

  ctrl.isSelfService = LocalUserPermissions.isSelfService();
  ctrl.isAxAdvertiser = LocalUserPermissions.isAxAdvertiser();
  ctrl.isAdOps = LocalUserPermissions.isAdOps();

  return {
    setUpTiers() {
      const enabledTiers = LocalNetworkProfile.get('tiers'),
        currentTier = ctrl[param].tier;

      ctrl.tiers = _.cloneDeep(enabledTiers);

      if (currentTier) {
        const hasCurrent = !!_.find(ctrl.tiers, function(t) {
          return t.id === _.get(currentTier, 'id');
        });
        // add current tier to list if not in there already -- is probably disabled
        if (!hasCurrent) {
          ctrl.tiers.push(currentTier);
        }
      }

      _.forEach(ctrl.tiers, function(t: any) {
        t.displayName = t.name + (_.find(enabledTiers, {id: t.id}) ? '' : ' (disabled)');
      });

      if (!currentTier && ctrl.isNew && !ctrl.isSelfService) {
        ctrl[param].tier = _.find(ctrl.tiers, {id: LocalNetworkProfile.get('defaultTierId')});
      }
    },
    modDates: function(modStartDate, modEndDate) {
      lineItemModDates(adnTimeShifter, ctrl[param], modStartDate, modEndDate);
    },
    setSelfService: function() {
      ctrl.isSelfService = LocalUserPermissions.isSelfService();
    },
    setAxAdvertiser: function() {
      ctrl.isAxAdvertiser = LocalUserPermissions.isAxAdvertiser();
    },
    setObjectiveFields: function(product) {
      if (product && product.budgetSpecification === 'BUDGET_ONLY') {
        ctrl.objectiveFields = _.mapValues(DEFAULT_MARKETPLACE_OBJECTIVE_FIELDS, function() {
          return false;
        });
        ctrl.objectiveFields.BUDGET = true;
      } else if (product && product.type === 'CREATIVE_TAG' && ctrl.isAxAdvertiser) {
        ctrl.objectiveFields = {};
      } else {
        ctrl.objectiveFields = _.cloneDeep((!ctrl.showEverything || ctrl.isAxAdvertiser)
          ? LocalNetworkProfile.getDefaults().marketplaceObjectiveFields
          : LocalNetworkProfile.getDefaults().objectiveFields) || {};
      }
      if (product && product.objectiveSpecification === 'IMPRESSIONS' && !ctrl.objectiveFields.RENDERED_IMPRESSION) {
        ctrl.objectiveFields = {};
        ctrl.objectiveFields.RENDERED_IMPRESSION = true;
      }
      _.forEach(_.get(ctrl, [param, 'objectives']) || [], function(val: any, key: string) {
        if (hasConfiguredFormValue(val)) {
          ctrl.objectiveFields[key] = true;
        }
      });
      const oFieldsArray = _.filter(_.map(ctrl.objectiveFields, function(o: boolean, key: string) {
        return o ? key : false;
      }), function(o) {
        return o;
      });
      const singleObjective = oFieldsArray.length === 1;
      const singleBudgetObjective = singleObjective && oFieldsArray[0] === 'BUDGET';
      if (typeof ctrl.singleObjective?.set === 'function') {
        ctrl.singleObjective.set(singleObjective);
        ctrl.singleBudgetObjective.set(singleBudgetObjective);
      } else {
        ctrl.singleObjective = singleObjective;
        ctrl.singleBudgetObjective = singleBudgetObjective;
      }

      if (ctrl[param]) {
        ctrl[param].objectives = ctrl[param].objectives || {};
        const budget = ctrl[param].objectives.BUDGET;
        // API sometimes sends BUDGET: {}. Seed a real money shape before first CD —
        // leaving {} lets later nulling coerce hit NG0100 '{}'→'null'.
        if (ctrl.objectiveFields.BUDGET && (!budget || (_.isObject(budget) && !_.has(budget, 'amount')))) {
          ctrl[param].objectives.BUDGET = {
            amount: null,
            currency: (_.isObject(budget) && (budget as any).currency) || ctrl.moneyCurrency || null
          };
        }
      }

      const configuredRateLimitTypes = _.filter(_.map(LocalNetworkProfile.getDefaults().rateLimitFields, function(val: boolean, key: string) {
        return val ? key : false;
      }), function(val) {
        return val;
      });
      const currentRateLimitTypes = _.map(_.get(ctrl, [param, 'rateLimits']) || [], 'type');
      ctrl.rateLimitTypes = _.uniq(_.compact(configuredRateLimitTypes.concat(currentRateLimitTypes)));
    },
    setBidSpecificationFields: function(prod) {
      function setFields(ctrlParam: string) {
        ctrl[ctrlParam] = _.cloneDeep(ctrl[ctrlParam]) || {};
        if (prod) {
          ctrl[ctrlParam].cpc = _.get(prod.minimumCpc, ['currency']) && _.isFinite(_.get(prod.minimumCpc, ['amount']));
          ctrl[ctrlParam].cpm = _.get(prod.minimumCpm, ['currency']) && _.isFinite(_.get(prod.minimumCpm, ['amount']));
          ctrl[ctrlParam].cpa = false;
        }
        if (!prod || (!ctrl[ctrlParam].cpc && !ctrl[ctrlParam].cpm)) {
          ctrl[ctrlParam] = _.cloneDeep(LocalNetworkProfile.getDefaults()[ctrlParam]) || {};
        }
        if (_.get(ctrl, [param, 'type']) === 'CREATIVE_TAG' || _.get(prod, ['type']) === 'CREATIVE_TAG') {
          ctrl[ctrlParam].cpc = false;
          ctrl[ctrlParam].cpa = false;
          ctrl[ctrlParam].cpm = false;
        }
        _.forEach(_.get(ctrl, [param, 'bidSpecification']) || [], function(val: any, key: string) {
          const hasValue = key === 'cpa'
            ? hasConfiguredFormValue(_.get(val, 'cost'))
            : hasConfiguredFormValue(val);
          if (_.includes(['cpm', 'cpc', 'cpa'], key) && hasValue) {
            ctrl[ctrlParam][key] = true;
          }
        });
        if (!ctrl[ctrlParam].cpm && !ctrl[ctrlParam].cpc && !ctrl[ctrlParam].cpa) {
          ctrl[ctrlParam].cpm = true;
        }
      }

      setFields('bidSpecificationFields');
      setFields('marketplaceBidSpecificationFields');
    },
    setObjectivesAsString: function() {
      const methodName = pParam ? pParam + "ObjectivesAsString" : "objectivesAsString";
      ctrl[methodName] = function() {
        let objectives: ObjectiveSummary[] = [];
        _.forEach(ctrl[param].objectives, function(o: any, key: string) {
          const objData: ObjectiveSummary = {
            number: o,
            key: key,
            translation: ogTranslate(
              'lineItem.objective.' + key + '.plural',
              undefined,
              function(k: string) { return LocalNetworkProfile.getNetworkTranslations(k); },
              function(k: string, params: any) { return translate.instant(k, params); }
            ).toLowerCase()
          };
          const amount = _.get(o, 'amount');
          if (amount) {
            const managedAmount = _.isString(amount) ? parseFloat(amount) : amount;
            objData.prettyNumber = formatIsoCurrency(managedAmount, _.get(o, 'currency'), undefined, undefined, undefined, LocalNetworkProfile.get('defaultCurrency'), appLocale.getLocaleId());
          } else if (_.isFinite(o) || (_.isString(o) && _.trim(o).length > 0)) {
            objData.prettyNumber = formatLocaleNumber(o, undefined, appLocale.getLocaleId());
          }
          if (objData.prettyNumber) {
            objectives.push(objData);
          }
        });
        if (objectives.length === 0) {
          return "No objectives specified";
        }
        objectives = _.orderBy(objectives, function(d: ObjectiveSummary) {
          return LINE_ITEM_OBJECTIVE_TYPE[d.key].order;
        });
        let returnString = "";
        _.forEach(objectives, function(d: ObjectiveSummary, index: number) {
          returnString += d.prettyNumber + " " + d.translation;
          if (index + 1 < objectives.length) {
            returnString += " or ";
          }
        });
        return returnString;
      };
    }
  };
};

export function enrichLineItemList(
  list: any,
  LocalNetworkProfile: any,
  adnTimeShifter: any,
  translate: any,
  appLocale: any,
  LocalUserPermissions: any,
  localNetworkInfo: LocalNetworkInfo
): void {
  _.forEach(list.items, function(li: any) {
    if (li.startDate) {
      li.modStartDate = li.startDate;
    }
    if (li.endDate) {
      li.modEndDate = li.endDate;
    }
  });

  const commons = lineItemCommons(
    list,
    translate,
    appLocale,
    LocalNetworkProfile,
    LocalUserPermissions,
    adnTimeShifter,
    localNetworkInfo
  );
  commons.setObjectiveFields();
}

export function configureLineItemList(
  ctrl: any,
  LocalNetworkProfile: any,
  adnTimeShifter: any,
  translate: any,
  appLocale: any,
  LocalUserPermissions: any,
  teamApi: TeamApiService,
  Order: any,
  localNetworkInfo: LocalNetworkInfo,
  view: string
): void {
  enrichLineItemList(ctrl, LocalNetworkProfile, adnTimeShifter, translate, appLocale, LocalUserPermissions, localNetworkInfo);

  ctrl.getOrders = function() {
    if (!ctrl.orders && ctrl.advertiserId) {
      ctrl.gettingOrders = true;
      Order.listOptions({minimal: true, advertiser: ctrl.advertiserId}).subscribe(function(page: any) {
        ctrl.orders = page.results;
        ctrl.gettingOrders = false;
      });
    }
  };
  ctrl.assignAdvertiser = function(advertiserId: string) {
    ctrl.advertiserId = advertiserId;
  };

  ctrl.teamsCount = localNetworkInfo.getInfo('teamsCount');
  ctrl.getTeams = function() {
    if (!ctrl.teams && ctrl.teamsCount > 1 && ctrl.teamsCount <= 15) {
      ctrl.gettingTeams = true;
      teamApi.listOptions({minimal: true}).subscribe(function(page) {
        ctrl.teams = page.results;
        ctrl.gettingTeams = false;
      });
    }
  };
  ctrl.blockTeams = function() {
    ctrl.teamsCount = 0;
  };

  ctrl.countries = _.orderBy(_.map(localNetworkInfo.getTeamCountriesSet(), function(c: string) {
    return {
      id: c,
      expanded: getCountryName(c)
    };
  }), ['id']);

  ctrl.targetingTypes = ALL_TARGETING_TYPES;
  ctrl.hasLineItemTemplatesCount = localNetworkInfo.getInfo('lineItemTemplatesCount') > 0;
  ctrl.hasObjectSource = LocalNetworkProfile.hasObjectSource();
  ctrl.allFields = ['name', 'userState', 'executionState'];
  if (view === 'LINE_ITEM_VIEW') {
    ctrl.allFields.push({label: 'advertiser', param: 'ReadOnlyAdvertiser'});
  }
  if (view !== 'ORDER_VIEW') {
    ctrl.allFields.push({label: 'order', param: 'Order'});
  }
  ctrl.allFields = ctrl.allFields.concat([{label: 'tier', param: 'Tier'}, {param: 'objectives', numeric: true}, {label: 'bids', param: '', numeric: true}, 'startDate', 'endDate', {label: 'startDate', param: 'startDate|withTime', otherLabel: 'startDateAndTime'}, {label: 'endDate', param: 'endDate|withTime', otherLabel: 'endDateAndTime'}, 'createUser', 'updateTime', {label: 'targeting', param: ''}]);
  if (ctrl.hasObjectSource) {
    ctrl.allFields.splice(1, 0, 'source');
  }
  if (!ctrl.perms?.MANAGE_ADVERTISING) {
    ctrl.allFields = ['name', 'userState', 'executionState', {label: 'order', param: 'Order'}, 'objectives', {label: 'bids', param: '', numeric: true}, 'startDate', 'endDate', {label: 'startDate', param: 'startDate|withTime', otherLabel: 'startDateAndTime'}, {label: 'endDate', param: 'endDate|withTime', otherLabel: 'endDateAndTime'}, 'createUser', 'updateTime'];
  }
  ctrl.allFields.push({param: "spendDelivery", numeric: true});
  ctrl.allFields.push({labelInstant: LocalNetworkProfile.getNetworkTranslations('impression'), param: "impressionDelivery", numeric: true});
  ctrl.allFields.push({labelInstant: LocalNetworkProfile.getNetworkTranslations('rendered'), param: "renderedDelivery", numeric: true});
  ctrl.allFields.push({param: "viewableDelivery", numeric: true});
  ctrl.allFields.push({param: "clickDelivery", numeric: true});
  ctrl.allFields.push({param: "viewability", numeric: true});
  ctrl.allFields.push({param: "", label: "eCpm", numeric: true});
  ctrl.allFields.push({param: "", label: "cpc", numeric: true});
  ctrl.allFields.push({param: "ctr", numeric: true});
  ctrl.allFields.push({param: "deliveryPercentage", numeric: true});
  ctrl.allFields.push({param: "elapsedTimePercentage", numeric: true});
  ctrl.allFields.push({param: "deliveryPercentageYesterday", label: 'deliveryPercentageYesterdaySmall', otherLabel: 'deliveryPercentageYesterday', numeric: true});
  ctrl.allFields.push({param: "deliveryPercentageToday", label: 'deliveryPercentageTodaySmall', otherLabel: 'deliveryPercentageToday', numeric: true});
  ctrl.allFields.push({param: "velocity|progress", label: 'progress'});
  ctrl.allFields.push({param: "velocity", label: "progressSlim"});
  ctrl.allFields.push({param: 'velocity|progressFull', label: 'progressFull'});

  if (view === 'SLIM') {
    ctrl.allFields = ['name', 'userState', 'executionState', 'startDate', 'endDate'];
  }

  ctrl.view = view;
  const skipForDefaults = ctrl.perms.AX_ADVERTISER || ctrl.perms.AX_ADVERTISER_ADMIN || ctrl.perms.AX_ADVERTISER_TEAM_ADMIN ?
    ['source', 'advertiser', 'order', 'createUser', 'updateTime', 'bids', 'targeting', 'spendDelivery', 'impressionDelivery', 'clickDelivery', 'viewability', 'eCpm', 'cpc', 'viewableDelivery', 'ctr', 'startDateAndTime', 'endDateAndTime', 'deliveryPercentage', 'elapsedTimePercentage', 'deliveryPercentageYesterday', 'deliveryPercentageToday', 'velocity|progress', 'velocity|progressFull'] :
    ['source', 'advertiser', 'order', 'createUser', 'updateTime', 'bids', 'targeting', 'spendDelivery', 'impressionDelivery', 'renderedDelivery', 'viewability', 'eCpm', 'cpc', 'viewableDelivery', 'clickDelivery', 'ctr', 'startDateAndTime', 'endDateAndTime', 'deliveryPercentage', 'elapsedTimePercentage', 'deliveryPercentageYesterday', 'deliveryPercentageToday', 'velocity|progress', 'velocity|progressFull'];
  ctrl.defaultFields = _.filter(ctrl.allFields, function(f: any) {
    return skipForDefaults.indexOf(f.label || f.param || f) < 0;
  });
  ctrl.sel = {};
}
